/**
 * A plane's marks as one SVG, for surfaces that show them without an open
 * canvas (the 3D view, once you orbit off a plane). tldraw draws them, in a
 * throwaway headless editor, so they look exactly as they did when drawn.
 */
import type { Plane, Rect } from "playground/annotations/annotations";
import {
  createTLStore,
  Editor,
  defaultAddFontsFromNode,
  tipTapDefaultExtensions,
} from "tldraw";

import { loadPlane, planePageId } from "@/review/plane-store";
import {
  REVIEW_BINDING_UTILS,
  REVIEW_SHAPE_UTILS,
} from "@/review/review-shapes";
import { SKETCH_ASSET_URLS } from "@/sketch/sketch-assets";

/** Room around the marks for stroke width and lettering, in plane units. */
const MARGIN = 16;

export interface PlaneImage {
  svg: string;
  /** The plane-unit rectangle the SVG covers. */
  bounds: Rect;
}

/** The marks drawn, or null when there are none. */
export async function planeSvg(plane: Plane): Promise<PlaneImage | null> {
  const store = createTLStore({
    shapeUtils: REVIEW_SHAPE_UTILS,
    bindingUtils: REVIEW_BINDING_UTILS,
  });
  loadPlane(store, plane);
  // Lettering measures against a real element, so the editor needs one in the
  // document, out of sight.
  // It is a tldraw container, as TldrawImage's is: lettering measures in
  // tldraw's font variables, which that class defines.
  const container = document.createElement("div");
  container.classList.add("tl-container", "tl-theme__light");
  container.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1px;height:1px";
  document.body.appendChild(container);
  const editor = new Editor({
    store,
    shapeUtils: REVIEW_SHAPE_UTILS,
    bindingUtils: REVIEW_BINDING_UTILS,
    tools: [],
    getContainer: () => container,
    fontAssetUrls: SKETCH_ASSET_URLS.fonts,
    // What <Tldraw> supplies a mounted editor: without it lettering throws.
    options: {
      text: {
        tipTapConfig: { extensions: tipTapDefaultExtensions },
        addFontsFromNode: defaultAddFontsFromNode,
      },
    },
  });
  try {
    editor.setCurrentPage(planePageId(plane.id));
    // Bounds are measured in the lettering's own face, so it must be loaded.
    await editor.fonts.loadRequiredFontsForCurrentPage();
    const ids = [...editor.getCurrentPageShapeIds()];
    const marks = editor.getShapesPageBounds(ids);
    if (!marks) return null;
    const bounds = marks.clone().expandBy(MARGIN);
    const image = await editor.getSvgString(ids, {
      bounds,
      padding: 0,
      background: false,
    });
    if (!image) return null;
    return {
      svg: image.svg,
      bounds: { x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h },
    };
  } finally {
    editor.dispose();
    container.remove();
  }
}
